import { downsampleToPcm16 } from '@/lib/meetings/segmenter'
import { MIC_WORKLET_NAME, micWorkletUrl } from '@/lib/meetings/worklet'

/** What the Soniox realtime socket is told to expect (`pcm_s16le`, mono). */
export const PCM_SAMPLE_RATE = 16_000

/**
 * Microphone → 16 kHz mono PCM16 frames, the format the meeting path already
 * proves in production (`web/lib/meetings/transcriber.ts`). Reused here for
 * dictation because it is one code path on every browser — `MediaRecorder`
 * would give Safari `audio/mp4` and Chrome `audio/webm;codecs=opus`.
 *
 * Thin: the worklet posts 20 ms Float32 frames at the context rate, this
 * downsamples them and hands over Int16 frames. No VAD — the provider does
 * endpoint detection.
 */
export interface PcmMic {
  start(stream: MediaStream, onFrame: (pcm: Int16Array) => void): Promise<void>
  stop(): Promise<void>
}

export function createPcmMic(): PcmMic {
  let context: AudioContext | null = null
  let node: AudioWorkletNode | null = null
  let source: MediaStreamAudioSourceNode | null = null
  let stopped = false
  return {
    async start(stream, onFrame) {
      if (stopped || context) return
      const ctx = new AudioContext()
      context = ctx
      await ctx.audioWorklet.addModule(micWorkletUrl())
      if (stopped) return
      node = new AudioWorkletNode(ctx, MIC_WORKLET_NAME)
      node.port.onmessage = (event: MessageEvent<Float32Array>) => {
        if (stopped) return
        onFrame(downsampleToPcm16(event.data, ctx.sampleRate, PCM_SAMPLE_RATE))
      }
      source = ctx.createMediaStreamSource(stream)
      source.connect(node)
      // No output connection: that would play the speaker back at themselves.
    },
    async stop() {
      stopped = true
      node?.port.close()
      source?.disconnect()
      node?.disconnect()
      await context?.close().catch(() => {})
      context = null
      node = null
      source = null
    },
  }
}
